import * as THREE from 'three'
import { Maze } from './world/Maze'
import { Environment } from '../effects/Environment'
import { Bubbles } from '../effects/Bubbles'
import { BlueBob } from './entities/BlueBob'
import { Jellyfish } from './entities/Jellyfish'
import { Collectibles } from './entities/Collectibles'
import { GameState } from './GameState'
import { CameraRig } from '../engine/Camera'
import { captureScore, CAPTURE_JELLY } from './scoring-rules'
import {
  MOVE_SPEED,
  ENEMY_SPEED,
  POWER_UP_DURATION,
  POWER_UP_SPEED_MULT,
  DOT_POINTS,
  POWER_PELLET_POINTS,
  SCATTER_DURATION,
  CHASE_DURATION,
  JELLY_PER_LEVEL_CLEAR,
} from '../constants/game-config'
import type { GridPos, JellyKind, Quality } from '../types'
import { diag } from '../utils/diagnostics'
import { getLevelTheme, LEVEL_COUNT } from '../data/level-themes'
import { gridToWorld } from '../utils/grid'
import { cloneModelTemplate, loadModel } from '../utils/glb-loader'

export interface GameAudio {
  play(name: string): void
  startAmbient(): void
  stopAmbient(): void
}

const PLAYER_START: GridPos = { row: 21, col: 13 }
const SHELL_POINTS = 50

interface JellySpec {
  kind: JellyKind
  start: GridPos
  corner: GridPos
  release: number
}

const JELLY_SPECS: JellySpec[] = [
  { kind: 'chaser', start: { row: 11, col: 13 }, corner: { row: 1, col: 26 }, release: 0 },
  { kind: 'ambusher', start: { row: 13, col: 13 }, corner: { row: 1, col: 1 }, release: 2 },
  { kind: 'patroller', start: { row: 13, col: 14 }, corner: { row: 27, col: 26 }, release: 4.5 },
  { kind: 'wanderer', start: { row: 14, col: 13 }, corner: { row: 27, col: 1 }, release: 7 },
]
const HOME: GridPos = { row: 14, col: 13 }

/** Strip shadow-casting from a subtree. The shadow map is frozen per level (see Renderer), so only
 * STATIC casters (maze walls / decor) belong in it — dynamic objects (player, jellies, collectibles,
 * boss) would otherwise be captured at one position and leave a "ghost" shadow as they move/vanish. */
function disableCastShadow(root: THREE.Object3D): void {
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = false
  })
}

/** Owns the playable world and runs the gameplay simulation. */
export class GameScene {
  readonly maze: Maze
  readonly environment: Environment
  readonly bubbles: Bubbles
  readonly player: BlueBob
  readonly jellies: Jellyfish[]
  private collectibles: Collectibles
  private scene: THREE.Scene
  private camera: CameraRig
  private audio: GameAudio
  readonly state: GameState

  private globalMode: 'scatter' | 'chase' = 'scatter'
  private modeTimer = SCATTER_DURATION
  private powerTimer = 0
  private readyTimer = 0
  private deathTimer = 0
  private clearTimer = 0
  private playerSpeed = MOVE_SPEED
  private advancing = false
  /** The Jelly King boss model (Blender GLB) — present only on the final Showdown level. */
  private boss: THREE.Group | null = null
  private bossToken = 0
  private bossT = 0
  /** Set by GameApp: play the transition cutscene for `nextLevel`, then call advanceLevel(). */
  onAdvance: ((nextLevel: number) => void) | null = null
  /** Set by GameApp: play the ending cutscene after the final level is cleared. */
  onStoryComplete: (() => void) | null = null

  constructor(scene: THREE.Scene, camera: CameraRig, audio: GameAudio, state: GameState, quality: Quality) {
    this.scene = scene
    this.camera = camera
    this.audio = audio
    this.state = state

    this.maze = new Maze(1, getLevelTheme(1))
    scene.add(this.maze.group)
    this.environment = new Environment(scene, quality, camera.cam)
    this.environment.applyTheme(getLevelTheme(1))
    this.bubbles = new Bubbles(quality === 'low' ? 120 : 220)
    scene.add(this.bubbles.group)

    this.player = new BlueBob(PLAYER_START.row, PLAYER_START.col, this.maze)
    scene.add(this.player.group)
    scene.add(this.player.trailGroup) // world-space cosmetic trail (must not follow the player)

    this.jellies = JELLY_SPECS.map((s) => {
      const j = new Jellyfish(s.kind, s.start, s.corner, HOME, this.maze, s.release)
      scene.add(j.group)
      return j
    })

    this.collectibles = new Collectibles(this.maze, this.scene.environment)
    scene.add(this.collectibles.group)
    this.state.pearlsRemaining = this.collectibles.remaining

    // Dynamic objects must not cast into the frozen shadow map (see disableCastShadow). Only the maze
    // walls + static decor cast; the key light + shadow camera are fixed, so those shadows never change.
    disableCastShadow(this.player.group)
    this.jellies.forEach((j) => disableCastShadow(j.group))
    disableCastShadow(this.collectibles.group)
  }

  /** Set by GameApp → Renderer.refreshShadows(): re-render the frozen shadow map once after the
   * world's static casters change (level rebuild), so the new maze's wall shadows are captured. */
  onWorldRebuilt: (() => void) | null = null

  setPlayerDesired(dir: BlueBob['desired']): void {
    this.player.desired = dir
  }

  /** Dev/E2E only: jump straight to a level, rebuilding its maze + theme. */
  devGotoLevel(n: number): void {
    this.state.level = Math.max(1, Math.min(LEVEL_COUNT, n))
    this.rebuildLevel()
    this.placeEntities()
    this.beginReady()
  }

  /** QA shortcut: enter the same clear/reward/story flow as collecting the last pearl. */
  devCompleteLevel(): void {
    if (this.state.status !== 'playing') return
    this.collectibles.remaining = 0
    this.state.pearlsRemaining = 0
    this.levelClear()
  }

  /** Apply the player's equipped cosmetics (hat / skin / trail) to the BlueBob model. */
  setPlayerCosmetics(eq: import('../data/cosmetics').EquippedCosmetics): void {
    this.player.applyCosmetics(eq)
    // Cosmetic factories (e.g. the sailor hat) attach NEW meshes to the player group with
    // castShadow=true. The shadow map is frozen, so a hat added after the constructor's one-time
    // disableCastShadow would be baked once at spawn position and ghost as the player moves. Re-strip.
    disableCastShadow(this.player.group)
  }

  // --------------------------------------------------------------------------
  // Lifecycle
  // --------------------------------------------------------------------------
  newGame(): void {
    this.advancing = false
    this.state.resetRun()
    this.rebuildLevel()
    this.placeEntities()
    this.audio.startAmbient()
    this.beginReady()
  }

  private rebuildLevel(): void {
    const lvl = this.state.level
    const theme = getLevelTheme(lvl)
    // Rebuild the maze geometry + layout for this level FIRST, then re-theme the atmosphere,
    // so the collectible spawner + AI walkability read the NEW grid. Entities hold the same
    // Maze instance (rebuilt in place), so no re-creation is needed.
    this.maze.rebuild(lvl, theme)
    this.environment.applyTheme(theme)
    this.placeBoss(lvl)

    this.collectibles.dispose()
    this.scene.remove(this.collectibles.group)
    this.collectibles = new Collectibles(this.maze, this.scene.environment)
    this.scene.add(this.collectibles.group)
    disableCastShadow(this.collectibles.group)
    this.state.pearlsRemaining = this.collectibles.remaining

    this.playerSpeed = MOVE_SPEED + (lvl - 1) * 0.15
    const enemySpeed = ENEMY_SPEED + (lvl - 1) * 0.25
    for (const j of this.jellies) j.setBaseSpeed(enemySpeed)

    // New maze geometry → re-render the frozen shadow map once so its wall shadows are captured.
    this.onWorldRebuilt?.()
  }

  /** Show the Jelly King boss (Blender GLB) looming over the maze center — final level only.
   * Async-load is guarded by `bossToken` so a level change mid-load never leaves a stale model. */
  private placeBoss(lvl: number): void {
    const token = ++this.bossToken
    if (this.boss) {
      // Detach only — geometry/materials are owned by the loader cache and reused across visits,
      // so we must NOT dispose them here (the boss is the same cached object each time).
      this.scene.remove(this.boss)
      this.boss = null
    }
    if (lvl !== LEVEL_COUNT) return
    const { x, z } = gridToWorld(13, 14) // ghost-house / maze center
    loadModel('/models/king.glb')
      .then((template) => {
        if (token !== this.bossToken) return // superseded by a later rebuild
        // The cutscene stage shares the cached template, so gameplay gets its own transform tree.
        const model = cloneModelTemplate(template)
        model.scale.setScalar(2.4)
        model.position.set(x, 1.6, z)
        disableCastShadow(model) // boss bobs (bossT) — would ghost in the frozen map
        this.scene.add(model)
        this.boss = model
        this.bossT = 0
      })
      .catch((err) => {
        // Model missing/failed → no boss decoration, gameplay unaffected. The loader evicts the
        // failed cache entry so a later level-10 visit retries. Surface it in DEV for visibility.
        if (import.meta.env.DEV) console.warn('[boss] king.glb failed to load:', err)
      })
  }

  private placeEntities(): void {
    this.player.reset()
    this.player.setSpeed(this.playerSpeed)
    this.jellies.forEach((j, i) => j.reset(JELLY_SPECS[i].release))
    this.globalMode = 'scatter'
    this.modeTimer = SCATTER_DURATION
    this.powerTimer = 0
    this.state.powered = false
    this.state.powerRatio = 0
    this.camera.setFollowTarget(this.player.worldX, this.player.worldZ)
    this.camera.snapToFollow()
  }

  private beginReady(): void {
    this.readyTimer = 1.6
    this.state.setStatus('ready')
  }

  /** Advance to the next level — called by GameApp after the transition cutscene finishes. */
  advanceLevel(): void {
    this.state.level++
    this.rebuildLevel()
    this.placeEntities()
    this.beginReady()
    this.advancing = false
  }

  // --------------------------------------------------------------------------
  // Update — full simulation (called only while actively playing)
  // --------------------------------------------------------------------------
  update(dt: number): void {
    this.environment.update(dt)
    this.bubbles.update(dt)
    this.collectibles.update(dt)
    if (this.boss) {
      this.bossT += dt
      this.boss.rotation.y = Math.sin(this.bossT * 0.3) * 0.55
      this.boss.position.y = 1.6 + Math.sin(this.bossT * 1.1) * 0.14
    }

    switch (this.state.status) {
      case 'ready':
        this.readyTimer -= dt
        this.idleEntities(dt)
        if (this.readyTimer <= 0) this.state.setStatus('playing')
        break
      case 'playing':
        this.simulate(dt)
        break
      case 'dying':
        this.deathTimer -= dt
        this.animateDeath(dt)
        if (this.deathTimer <= 0) this.afterDeath()
        break
      case 'levelclear':
        this.clearTimer -= dt
        this.idleEntities(dt)
        if (this.clearTimer <= 0 && !this.advancing) {
          this.advancing = true // fire the boundary handling exactly once
          if (this.state.level >= LEVEL_COUNT) {
            // Final level cleared — the 10-chapter story is complete.
            this.state.won = true
            if (this.onStoryComplete) this.onStoryComplete()
            else this.state.setStatus('gameover')
          } else if (this.onAdvance) {
            // Play the transition cutscene; GameApp calls advanceLevel() when it ends.
            this.onAdvance(this.state.level + 1)
          } else {
            this.advanceLevel()
          }
        }
        break
      default:
        this.idleEntities(dt)
    }

    this.syncHarness()
  }

  /** Ambient-only update for attract mode / menus / pause. */
  idleUpdate(dt: number): void {
    this.environment.update(dt)
    this.bubbles.update(dt)
    this.collectibles.update(dt)
    this.idleEntities(dt)
    this.syncHarness()
  }

  private idleEntities(dt: number): void {
    this.player.idle(dt)
    for (const j of this.jellies) j.idle(dt)
  }

  // --------------------------------------------------------------------------
  private simulate(dt: number): void {
    // 1. global scatter/chase wave (paused while powered)
    if (this.powerTimer <= 0) {
      this.modeTimer -= dt
      if (this.modeTimer <= 0) {
        this.globalMode = this.globalMode === 'scatter' ? 'chase' : 'scatter'
        this.modeTimer = this.globalMode === 'scatter' ? SCATTER_DURATION : CHASE_DURATION
        for (const j of this.jellies) j.setGlobalMode(this.globalMode)
      }
    }

    // 2. player
    this.player.update(dt)
    this.camera.setFollowTarget(this.player.worldX, this.player.worldZ)

    // 3. jellyfish AI
    const chaser = this.jellies.find((j) => j.kind === 'chaser')!
    const ctx = {
      playerCell: this.player.cell,
      playerDir: this.player.dir,
      chaserCell: chaser.cell,
    }
    for (const j of this.jellies) j.update(dt, ctx, this.globalMode)

    // 4. pickups
    const got = this.collectibles.tryCollect(this.player.cell)
    if (got) this.onCollect(got.kind, got.x, got.z)
    this.state.pearlsRemaining = this.collectibles.remaining

    // 5. power timer
    if (this.powerTimer > 0) {
      this.powerTimer -= dt
      this.state.powerRatio = Math.max(0, this.powerTimer / this.powerDuration())
      const warn = this.powerTimer < 2
      if (warn) {
        const flash = Math.floor(this.powerTimer * 6) % 2 === 0
        for (const j of this.jellies) j.setWarning(flash)
      }
      if (this.powerTimer <= 0) this.endPower()
    }

    // 6. player vs jellyfish
    this.checkEnemyCollisions()

    // 7. level clear — but death this same frame takes priority over a transition
    if (this.collectibles.remaining <= 0 && this.state.status === 'playing') this.levelClear()

    this.state.emitHud()
  }

  private onCollect(kind: 'pearl' | 'shell' | 'power', x: number, z: number): void {
    if (kind === 'pearl') {
      this.state.addScore(DOT_POINTS)
      this.audio.play('pearl')
      this.bubbles.emitBurst(x, 0.4, z, 6, 0xcfeaff)
    } else if (kind === 'shell') {
      this.state.addScore(SHELL_POINTS)
      this.audio.play('shell')
      this.bubbles.emitBurst(x, 0.5, z, 10, 0xffd07a)
    } else {
      this.state.addScore(POWER_PELLET_POINTS)
      this.startPower()
      this.bubbles.emitBurst(x, 0.6, z, 18, 0xffd93d)
    }
  }

  private powerDuration(): number {
    return Math.max(2.5, POWER_UP_DURATION - (this.state.level - 1) * 0.5)
  }

  private startPower(): void {
    this.powerTimer = this.powerDuration()
    this.state.powered = true
    this.state.powerRatio = 1
    this.state.streak = 0
    this.player.setPowered(true)
    this.player.setSpeed(this.playerSpeed * POWER_UP_SPEED_MULT)
    for (const j of this.jellies) j.frighten()
    this.audio.play('power')
  }

  private endPower(): void {
    this.powerTimer = 0
    this.state.powered = false
    this.state.powerRatio = 0
    this.player.setPowered(false)
    this.player.setSpeed(this.playerSpeed)
    for (const j of this.jellies) j.unfrighten(this.globalMode)
  }

  private checkEnemyCollisions(): void {
    const px = this.player.worldX
    const pz = this.player.worldZ
    for (const j of this.jellies) {
      const dx = px - j.worldX
      const dz = pz - j.worldZ
      if (dx * dx + dz * dz > 0.45 * 0.45) continue
      if (j.isEaten) continue
      if (j.isEdible) {
        j.capture()
        this.state.streak++
        const pts = captureScore(this.state.streak)
        this.state.addScore(pts)
        this.state.jellyRun += CAPTURE_JELLY
        this.audio.play('capture')
        this.bubbles.emitBurst(j.worldX, 0.6, j.worldZ, 22, 0xff69b4)
      } else {
        this.loseLife()
        break
      }
    }
  }

  private loseLife(): void {
    this.state.lives--
    this.audio.play('death')
    if (this.state.lives <= 0) {
      this.endPower()
      this.state.setStatus('gameover')
    } else {
      this.deathTimer = 1.2
      this.endPower()
      this.state.setStatus('dying')
    }
  }

  private animateDeath(dt: number): void {
    const k = Math.max(0, this.deathTimer / 1.2)
    this.player.group.scale.setScalar(Math.max(0.0001, k))
    this.player.group.rotation.y += dt * 8
  }

  private afterDeath(): void {
    this.player.group.scale.setScalar(1)
    this.player.group.rotation.y = 0
    this.placeEntities()
    this.beginReady()
  }

  private levelClear(): void {
    this.state.jellyRun += JELLY_PER_LEVEL_CLEAR
    this.audio.play('levelclear')
    this.clearTimer = 2.2
    this.endPower()
    this.state.setStatus('levelclear')
  }

  private syncHarness(): void {
    // Dev/E2E only — skip the per-frame harness snapshot in production builds.
    if (!import.meta.env.DEV) return
    const h = diag()
    h.hud = this.state.hud()
    const px = this.player.worldX
    const pz = this.player.worldZ
    h.player = {
      row: this.player.cell.row,
      col: this.player.cell.col,
      x: px,
      z: pz,
      dir: this.player.dir,
    }
    h.jellies = this.jellies.map((j) => {
      const dx = px - j.worldX
      const dz = pz - j.worldZ
      return {
        kind: j.kind,
        row: j.cell.row,
        col: j.cell.col,
        mode: j.mode,
        dist: Math.round(Math.sqrt(dx * dx + dz * dz) * 100) / 100,
      }
    })
    h.match = this.state.status
    h.cosmetics = this.player.cosmeticsInfo()
    h.mazeSig = this.maze.signature()
    h.themeName = getLevelTheme(this.state.level).name
  }
}
